import type { Reference } from '@medplum/fhirtypes'
import { featureFlags } from '@seen/core/featureFlags'
import {
  DocumentSource,
  InlinePasteProcessingStatus,
} from '@seen/core/models/document-references/constants'
import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import { getReferenceString } from '@seen/core/references'
import { isOfType } from '@seen/core/utils'
import FileHandler from '@tiptap/extension-file-handler'
import { uniqBy } from 'lodash-es'
import { useMemo } from 'react'
import { useOptionalParticipantReference } from '#src/contexts/current-participant/useOptionalParticipantReference.ts'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { createAttachmentFromFile } from '#src/utils/createAttachmentFromFile.ts'

export function useFileHandler(related?: Reference[]): typeof FileHandler {
  const medplum = useMedplum()
  const participantReference = useOptionalParticipantReference()
  const practitioner = useCurrentPractitioner()
  const groupedProcessingEnabled = useFeatureFlag(
    featureFlags.inlinePasteDocumentProcessing
  )
  const { mutateAsync: createDocument } =
    useCreateMutation<ExtendedDocumentReference>()
  const fileHandler = useMemo(() => {
    const createDocumentFromFiles = async (
      files: File[]
    ): Promise<ExtendedDocumentReference> => {
      const attachments = await Promise.all(
        files.map(async (file) => {
          const attachment = await createAttachmentFromFile(file, { medplum })
          return { ...attachment, size: attachment.size ?? file.size }
        })
      )
      const relatedParticipant = groupedProcessingEnabled
        ? related?.find((reference) => isOfType(reference, 'Patient'))
        : undefined
      const contextRelated = groupedProcessingEnabled
        ? uniqBy(
            related?.filter((reference) => !isOfType(reference, 'Patient')),
            getReferenceString
          )
        : related
      const draft = new ExtendedDocumentReference({
        subject: participantReference ?? relatedParticipant,
        author: practitioner.reference,
        attachments,
        context: groupedProcessingEnabled
          ? contextRelated?.length
            ? { related: contextRelated }
            : undefined
          : related && { related },
      })
      draft.source = DocumentSource.INLINE_PASTE
      const hasProcessableImage = attachments.some(
        (attachment) =>
          attachment.contentType === 'image/jpeg' ||
          attachment.contentType === 'image/png'
      )
      if (groupedProcessingEnabled && hasProcessableImage) {
        draft.inlinePasteProcessingStatus = InlinePasteProcessingStatus.PENDING
      }
      return createDocument(draft)
    }

    const fileGroups = (files: File[]): File[][] =>
      groupedProcessingEnabled && files.length > 0
        ? [files]
        : files.map((file) => [file])

    return FileHandler.configure({
      onDrop: (currentEditor, files, pos) => {
        void Promise.all(
          fileGroups(files).map(async (group) => {
            const documentReference = await createDocumentFromFiles(group)
            currentEditor
              .chain()
              .insertContentAt(pos, {
                type: 'document-reference',
                attrs: {
                  reference: documentReference.reference.reference,
                  display: documentReference.title,
                },
              })
              .focus()
              .run()
          })
        )
      },
      onPaste: (currentEditor, files, htmlContent) => {
        if (htmlContent) {
          return
        }
        void Promise.all(
          fileGroups(files).map(async (group) => {
            const documentReference = await createDocumentFromFiles(group)
            currentEditor
              .chain()
              .insertContentAt(currentEditor.state.selection.anchor, {
                type: 'document-reference',
                attrs: {
                  reference: documentReference.reference.reference,
                  display: documentReference.title,
                },
              })
              .focus()
              .run()
          })
        )
      },
    })
  }, [
    createDocument,
    groupedProcessingEnabled,
    medplum,
    participantReference,
    practitioner,
    related,
  ])
  return fileHandler
}
